import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@pericles/framework-frontend";

import { generateProjectBrief, fetchProjectBrief, updateProjectBrief } from "../api/projectBriefClient";
import { createProjectMeeting } from "../api/projectMeetingsClient";
import type { ProjectBriefRecord } from "../types/projectBrief";
import type { ProjectConversationMessage } from "../types/projectConversations";

import { InlineAlert } from "./InlineAlert";
import { ProjectConversationPanel } from "./ProjectConversationPanel";
import { ProjectSpecificationPanel } from "./ProjectSpecificationPanel";

interface ProjectInputDataPanelProps {
  projectId: string;
  projectName?: string;
}

export function ProjectInputDataPanel({ projectId, projectName }: ProjectInputDataPanelProps): JSX.Element {
  const [projectBrief, setProjectBrief] = useState<ProjectBriefRecord | null>(null);
  const [projectContext, setProjectContext] = useState<string>("");
  const [loadingBrief, setLoadingBrief] = useState<boolean>(false);
  const [savingContext, setSavingContext] = useState<boolean>(false);
  const [contextNotice, setContextNotice] = useState<string | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);
  const [conversationMessages, setConversationMessages] = useState<ProjectConversationMessage[]>([]);
  const [noteNotice, setNoteNotice] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [savingNote, setSavingNote] = useState<boolean>(false);

  useEffect(() => {
    setProjectBrief(null);
    setProjectContext("");
    setContextNotice(null);
    setContextError(null);
    setNoteError(null);
    setNoteNotice(null);
    void loadProjectBrief();
  }, [projectId]);

  const lastAssistantMessage = useMemo(
    () =>
      [...conversationMessages]
        .filter((message) => message.role?.toLowerCase?.() === "assistant")
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .slice(-1)[0],
    [conversationMessages],
  );

  const lastUserMessage = useMemo(
    () =>
      [...conversationMessages]
        .filter((message) => message.role?.toLowerCase?.() === "user")
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .slice(-1)[0],
    [conversationMessages],
  );

  const loadProjectBrief = async () => {
    if (!projectId) return;

    setLoadingBrief(true);
    setContextError(null);

    try {
      const brief = await fetchProjectBrief(projectId);
      setProjectBrief(brief);

      if (brief?.content && typeof (brief.content as any).metadata === "object") {
        const metadata = (brief.content as any).metadata as Record<string, unknown>;
        if (typeof metadata.projectContext === "string") {
          setProjectContext(metadata.projectContext);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de charger le brief du projet.";
      setContextError(message);
    } finally {
      setLoadingBrief(false);
    }
  };

  const ensureBrief = async (): Promise<ProjectBriefRecord> => {
    if (projectBrief) return projectBrief;
    const generated = await generateProjectBrief(projectId);
    setProjectBrief(generated);
    return generated;
  };

  const handleSaveContext = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!projectId) return;

    setSavingContext(true);
    setContextError(null);
    setContextNotice(null);

    try {
      const baseBrief = await ensureBrief();
      const metadata = { ...(baseBrief.content as any).metadata, projectContext };
      const updated = await updateProjectBrief(projectId, { ...baseBrief.content, metadata } as any);
      setProjectBrief(updated);
      setContextNotice("Contexte du projet enregistré");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible d'enregistrer le contexte du projet.";
      setContextError(message);
    } finally {
      setSavingContext(false);
    }
  };

  const handleSaveAiNote = async () => {
    if (!projectId) return;
    if (!lastAssistantMessage) {
      setNoteError("Aucune réponse IA n'est disponible pour créer une note.");
      return;
    }

    setSavingNote(true);
    setNoteError(null);
    setNoteNotice(null);

    try {
      const questionsAndAnswers = [
        {
          question:
            lastUserMessage?.content ??
            "Échange IA (question utilisateur non disponible, conversation importée depuis l'IA)",
          answer: lastAssistantMessage.content,
        },
      ];

      const meeting = await createProjectMeeting(projectId, {
        title: `Note IA - ${projectName ?? "Projet"}`,
        templateKey: "ai-note",
        status: "done",
        questionsAndAnswers,
      });

      setNoteNotice(`Réponse IA enregistrée comme note de meeting (${meeting.title}).`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible d'enregistrer la note IA.";
      setNoteError(message);
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card title="Project context">
        <form className="flex flex-col gap-3" onSubmit={handleSaveContext}>
          <p className="text-sm text-slate-700">
            Rédigez un court contexte projet (objectifs, portée, contraintes). Il sera transmis aux assistants IA
            (analyse PDF, conversation) et pourra enrichir le Project Brief.
          </p>

          <label className="flex flex-col gap-1 text-sm text-slate-700">
            <span>Contexte projet</span>
            <textarea
              className="input"
              rows={4}
              placeholder="Contexte libre du projet..."
              value={projectContext}
              onChange={(event) => setProjectContext(event.target.value)}
              disabled={loadingBrief || savingContext}
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" className="button button-primary" disabled={savingContext}>
              {savingContext ? "Enregistrement..." : "Sauvegarder"}
            </button>
            <button
              type="button"
              className="button button-ghost"
              onClick={() => void loadProjectBrief()}
              disabled={loadingBrief || savingContext}
            >
              {loadingBrief ? "Chargement..." : "Recharger"}
            </button>
          </div>

          {contextNotice ? <InlineAlert tone="success" title={contextNotice} /> : null}
          {contextError ? <InlineAlert tone="danger" title={contextError} /> : null}
        </form>
      </Card>

      <Card title="Documents & analyse PDF">
        <p className="text-sm text-slate-700">
          Uploadez des PDF, ajoutez un contexte utilisateur puis déclenchez l'analyse IA. Les synthèses enrichissent
          l'usine à input.
        </p>
        <ProjectSpecificationPanel projectId={projectId} />
      </Card>

      <Card title="Conversation IA & notes">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-slate-700">
            Discutez avec l'agent IA avec le contexte du projet (brief, documents, notes). Vous pouvez ensuite conserver
            la dernière réponse en tant que note de meeting IA.
          </p>

          <ProjectConversationPanel
            projectId={projectId}
            onMessagesChange={setConversationMessages}
            onSpecUpdated={() => void loadProjectBrief()}
          />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => void handleSaveAiNote()}
              disabled={savingNote}
            >
              {savingNote ? "Enregistrement..." : "Sauvegarder la réponse IA en note"}
            </button>
            {lastAssistantMessage ? (
              <span className="text-xs text-slate-600">
                Dernière réponse IA capturée le {new Date(lastAssistantMessage.createdAt).toLocaleString()}
              </span>
            ) : (
              <span className="text-xs text-slate-500">Envoyez un message pour capturer une réponse IA.</span>
            )}
          </div>

          {noteNotice ? <InlineAlert tone="success" title={noteNotice} /> : null}
          {noteError ? <InlineAlert tone="danger" title={noteError} /> : null}
        </div>
      </Card>
    </div>
  );
}
